import React, { FC, useEffect, useState } from 'react';

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import styles from './PortfolioPowerplantsTable.module.scss';
import { PortfolioModel } from 'pages/PortfoliosPage/PortfolioModel';
import { PowerplantModel } from 'models/PowerplantModel';
import { PowerplantService } from 'services/PowerplantService';
import { PortfolioService } from 'pages/PortfoliosPage/PortfolioService';
import AutocompleteAssociationsTable from 'components/AutocompleteAssociationsTable/AutocompleteAssociationsTable';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface PortfolioPowerplantsTableProps {
  portfolio: PortfolioModel;
  isLoading: boolean;
  onPortfolioUpdate: (portfolioId: string) => void;
}

const PortfolioPowerplantsTable: FC<PortfolioPowerplantsTableProps> = (props: PortfolioPowerplantsTableProps) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [displayedPowerplants, setDisplayedPowerplants] = useState<{id: string, name: string}[]>([]);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [unassociatedPowerplants, setUnassociatedPowerplants] = useState<any[]>([]);
  const [powerplants, setPowerplants] = useState<PowerplantModel[]>([])

  useEffect(() => {
    getPowerplants();

    const powerplants = Object.entries(props.portfolio?.powerplants || {}).map(([key, value]) => ({
      id: key,
      name: value
    }));
    setDisplayedPowerplants(powerplants);
  }, [props.portfolio]);

  useEffect(() => {
    if (!powerplants || !displayedPowerplants) return;

    const powerplantIds = displayedPowerplants.map(powerplant => powerplant.id);
    const unassociatedPowerplants = powerplants.filter(powerplant => !powerplantIds.includes(powerplant._id));

    setUnassociatedPowerplants(unassociatedPowerplants);
  }, [displayedPowerplants, powerplants]);

  const getPowerplants = async (): Promise<void> => {
    try {
      const fetchedPowerplants: PowerplantModel[] = await PowerplantService.getPowerplants();

      if (!fetchedPowerplants) {
        throw new Error('No powerplants returned by the service.');
      }

      setPowerplants(fetchedPowerplants);
    } catch (error) {
      console.error('Failed at getting powerplants:', error);
      setPowerplants([]);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at getting powerplants. Please refresh, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }

  };

  const addPowerplantHandler = async (event: any, selectedPowerplant: any): Promise<void> => {
    if (!selectedPowerplant) return;

    try {
      const addPowerplantResult = await PortfolioService.addPowerplant(props.portfolio._id, selectedPowerplant._id, selectedPowerplant.name);

      if (!addPowerplantResult) {
        throw new Error('Error adding powerplant.');
      }

      props.onPortfolioUpdate(props.portfolio._id);
    } catch (error) {
      console.error('Failed at adding powerplant:', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at adding powerplant. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  };

  const removePowerplantHandler = async (powerplantId: any): Promise<void> => {
    if (!powerplantId) return;

    try {
      const removePowerplantResult = await PortfolioService.removePowerplant(props.portfolio._id, powerplantId)

      if (!removePowerplantResult) {
        throw new Error('Error removing powerplant.');
      }

      props.onPortfolioUpdate(props.portfolio._id);
    } catch (error) {
      console.error('Failed at removing powerplant:', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at removing powerplant. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  };

  const renderPowerplantActions = (params: GridRenderCellParams): JSX.Element => {
    return (
      <IconButton
        color="error"
        onClick={() => removePowerplantHandler(params.id)}
      >
        <DeleteIcon />
      </IconButton>
    )
  }

  const portfolioPowerplantColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'actions',
      headerName: '',
      width: 40,
      renderCell: renderPowerplantActions,
    },
  ];

  return (
    <div className={styles.PortfolioPowerplantsTable} data-testid="PortfolioPowerplantsTable">
      <AutocompleteAssociationsTable
        displayedItems={displayedPowerplants}
        unassociatedItems={unassociatedPowerplants}
        columns={portfolioPowerplantColumns}
        isLoading={props.isLoading}
        onAddItem={addPowerplantHandler}
        getOptionLabel={(option) => option.name}
        title="Powerplants"
      />

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  );
}

export default PortfolioPowerplantsTable;
